import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import type { StudioParameterPalette, StudioSwatch } from '../../../domain';
import { Swatch } from './Swatch';
import { swatchThumbnailUrl } from '../../ImageLab/swatchThumbnails';
import { paletteKindIcon } from './paletteIcons';
import { ColorModelPopover } from './ColorModelPopover';
import {
  resolveActiveSwatch,
  toggleSwatchSelection,
  type SwatchSelections,
} from './swatchSelection';

interface Props {
  palettes: StudioParameterPalette[];
  selections: SwatchSelections;
  onSelectionsChange: (next: SwatchSelections) => void;
  /** Studio id — keys the FE-bundled swatch thumbnail lookup. */
  studioId?: string;
}

/** Card edge length for thumbnail-driven (non-color) swatches. */
const CARD_SIZE = 72;

/**
 * Studio-driven swatch panel. Replaces the F10 placeholder with a real
 * palette renderer: each `StudioParameterPalette` becomes a labeled
 * group, and its swatches render via the kind-aware `Swatch` component.
 *
 * Selection state is owned by the parent (ImageLab.tsx). Clicking a
 * swatch toggles it on/off; clicking another swatch in the same palette
 * replaces. F14b/F-future will lift the selections into operation
 * request DTOs via `selectionsToParameterRecord` — out of scope for F14.
 */
export const SwatchPanel: React.FC<Props> = ({
  palettes,
  selections,
  onSelectionsChange,
  studioId,
}) => {
  // Color-inspector popover state (which color swatch is being inspected).
  const [inspect, setInspect] = React.useState<{ anchor: HTMLElement; swatch: StudioSwatch } | null>(
    null,
  );

  const handleSelect = (palette: StudioParameterPalette) => (swatch: StudioSwatch) => {
    onSelectionsChange(toggleSwatchSelection(selections, palette.id, swatch.id));
  };

  const handleInspect = (anchor: HTMLElement, swatch: StudioSwatch) => {
    setInspect({ anchor, swatch });
  };

  return (
    <Box
      sx={{
        // Fill the rail. The previous `width: 220` was the F14 default rail
        // width; once `RailSplitter` shipped (panel widen, 2026-05-18) the
        // hardcoded width started cutting swatches at ~50% of the rail.
        // Elevation pass 2026-05-26: swatches fill the rail.
        width: '100%',
        flexShrink: 0,
        p: 2,
        borderRight: theme => `1px solid ${theme.palette.divider}`,
        overflowY: 'auto',
      }}
      aria-label="Swatch panel"
    >
      <Stack spacing={2}>
        <Typography variant="overline" color="text.secondary">
          Swatches
        </Typography>
        {palettes.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            This Studio doesn't define parameter palettes.
          </Typography>
        )}
        {palettes.map(palette => {
          const active = resolveActiveSwatch(palette, selections);
          const PaletteIcon = paletteKindIcon(palette.kind);
          // Color palettes stay as bare circles; everything else renders as an
          // image card with the label below.
          const isCardPalette = palette.kind.toLowerCase() !== 'color';
          return (
            <Box key={palette.id}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <PaletteIcon fontSize="small" sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {palette.name}
                  </Typography>
                </Stack>
                {active && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {active.label}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} rowGap={1} flexWrap="wrap" useFlexGap>
                {palette.swatches.map(swatch => (
                  <Swatch
                    key={swatch.id}
                    swatch={swatch}
                    kind={palette.kind}
                    active={active?.id === swatch.id}
                    onSelect={handleSelect(palette)}
                    onInspect={handleInspect}
                    showLabel={isCardPalette}
                    size={isCardPalette ? CARD_SIZE : undefined}
                    bundledThumbnail={swatchThumbnailUrl(studioId, palette.id, swatch.id)}
                  />
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <ColorModelPopover
        open={!!inspect}
        anchorEl={inspect?.anchor ?? null}
        color={inspect?.swatch.value ?? ''}
        label={inspect?.swatch.label ?? ''}
        onClose={() => setInspect(null)}
      />
    </Box>
  );
};
